"use client";

import { useEffect } from "react";

interface ServiceWorkerRegistrationProps {
  /** App-Version (aus package.json) — haengt als ?v=<version> an die sw.js-URL
   *  und invalidiert so SW + Cache bei jedem App-Update (siehe Memory:
   *  PWA-Cache-Invalidation — Version NIE hardcoden, aus package.json lesen). */
  version: string;
  /** Pfad-Praefix, falls die App unter einem Sub-Pfad laeuft. Default: "". */
  basePath?: string;
}

/**
 * Geteilte SW-Registrierung fuer ALLE Janeway-Apps. Registriert
 * `<basePath>/sw.js?v=<version>` (Cache-Busting). Die app-spezifische sw.js liegt
 * in `public/` (network-first-Template) und uebernimmt via skipWaiting +
 * clients.claim selbst die Kontrolle fuer die naechste Navigation.
 *
 * BEWUSST KEIN `window.location.reload()` bei SW-Update — NIE wieder hinzufuegen:
 * Die sw.js ist network-first → jeder Document-Load holt bereits frisches HTML +
 * Assets vom Server. Beim App-Wechsel (AppSwitcher = voller Load) liefert somit
 * schon der ALTE SW die neue Version; ein Reload nach Aktivierung des neuen SW
 * laedt dieselbe Version ein ZWEITES Mal = sichtbarer DOPPELTER Splash-Screen
 * (gemeldeter Bug, 2026-06-19) und reisst aktive Nutzer mitten aus der Sitzung
 * (Verlust von Formular-Eingaben/Scroll). Der fruehere `hadController`-Guard hat
 * nur den Erst-Install abgedeckt, den Update-Fall aber nicht. Version-Skew bei
 * In-App-Routenwechseln faengt Next.js selbst ab; mehr braucht es nicht.
 */
// Vorgabe ist der GEBAUTE Basispfad: keine App reichte `basePath` durch, und mit
// BASE_PATH=/tasks registrierte sie `/sw.js` — eine Umleitung, die der Browser fuer
// Worker-Skripte verweigert („script resource is behind a redirect").
export function ServiceWorkerRegistration({ version, basePath = process.env.NEXT_PUBLIC_BASE_PATH ?? "" }: ServiceWorkerRegistrationProps) {
  useEffect(() => {
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;
    navigator.serviceWorker
      .register(`${basePath}/sw.js?v=${version}`, { scope: `${basePath}/` })
      .then((reg) => reg.update())
      .catch(() => {
        /* SW-Registrierung fehlgeschlagen — still ignorieren (App laeuft ohne PWA weiter) */
      });
  }, [version, basePath]);
  return null;
}
